import React from 'react';

import { ClientProfileFormValues, SelectControlOption } from 'types/state';
import { TFormValidation } from 'types/utils';
import { TIntlDictionary } from 'types/i18n';
import { OrgStaffCount } from 'types/orgInfoTypes';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextComponent } from 'design/text/textComponents';
import { SelectControlComponent, SelectOptionComponent } from 'design/select';
import { CountryInputComponent } from 'containers/CountryInputComponent';
import { CustomInputComponent } from 'design/customInput/customInputComponents';

type TProps = {
  values: ClientProfileFormValues;
  validation: TFormValidation<ClientProfileFormValues>;
  intlDictionary: TIntlDictionary;
  onChange(event: React.ChangeEvent): void;
  onOrgStaffCountChange(nextValue: OrgStaffCount): void;
  onLocationChange(nextValue: string): void;
};

export const ClientProfileInfoFormFieldset = (props: TProps) => {
  const {
    values,
    validation,
    intlDictionary,
    onChange,
    onOrgStaffCountChange,
    onLocationChange,
  } = props;

  const orgStaffCountOptions = React.useMemo<
    SelectControlOption<OrgStaffCount>[]
  >(() => {
    return [
      {
        value: 'small',
        title: intlDictionary.prodStackManager.clientInfoForm.staffCountText(
          'small'
        ),
      },
      {
        value: 'medium',
        title: intlDictionary.prodStackManager.clientInfoForm.staffCountText(
          'medium'
        ),
      },
      {
        value: 'large',
        title: intlDictionary.prodStackManager.clientInfoForm.staffCountText(
          'large'
        ),
      },
    ];
  }, [intlDictionary.prodStackManager.clientInfoForm]);

  return (
    <>
      <CustomInputComponent
        autoFocus
        value={values.lastName}
        errorMessage={validation.lastName}
        placeholder={intlDictionary.lastNameInputPlaceholder}
        name="lastName"
        onChange={onChange}
      />
      <CustomInputComponent
        value={values.firstName}
        errorMessage={validation.firstName}
        placeholder={intlDictionary.firstNameInputPlaceholder}
        name="firstName"
        onChange={onChange}
      />
      <CustomInputComponent
        value={values.middleName}
        errorMessage={validation.middleName}
        placeholder={intlDictionary.middleNameInputPlaceholder}
        name="middleName"
        onChange={onChange}
      />
      <CustomInputComponent
        value={values.position}
        errorMessage={validation.position}
        placeholder={intlDictionary.positionInputPlaceholder}
        name="position"
        onChange={onChange}
      />
      <CustomInputComponent
        value={values.company}
        errorMessage={validation.company}
        placeholder={intlDictionary.companyInputPlaceholder}
        name="company"
        onChange={onChange}
      />
      <FloatingLabelComponent
        lifted={!!values.orgStaffCount}
        invalid={!!validation.orgStaffCount}
        titleText={
          <TextComponent color="secondary" size="m">
            {intlDictionary.staffInputPlaceholder}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {validation.orgStaffCount}
          </TextComponent>
        }
      >
        <SelectControlComponent
          name="staff"
          invalid={!!validation.orgStaffCount}
          value={`${values.orgStaffCount}`}
          onValueChange={onOrgStaffCountChange}
        >
          <SelectOptionComponent disabled value="null">
            {''}
          </SelectOptionComponent>
          {orgStaffCountOptions.map((option) => (
            <SelectOptionComponent key={option.value} value={option.value}>
              {option.title}
            </SelectOptionComponent>
          ))}
        </SelectControlComponent>
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.location}
        invalid={!!validation.location}
        titleText={
          <TextComponent color="secondary" size="m">
            {intlDictionary.locationInputPlaceholder}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {validation.location}
          </TextComponent>
        }
      >
        <CountryInputComponent
          name="location"
          value={values.location}
          invalid={!!validation.location}
          onChange={onLocationChange}
        />
      </FloatingLabelComponent>
      <CustomInputComponent
        value={values.phone}
        errorMessage={validation.phone}
        placeholder={intlDictionary.phoneInputPlaceholder}
        name="phone"
        onChange={onChange}
      />
    </>
  );
};
